import { useEffect, useState } from 'react';
import { RequirePermission } from '../components/NoAccess';
import { formatDate, formatDateTime } from '../../../lib/format';
import { BRAND, EXAM_TYPE_LABEL } from '@dru/types';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  AppointmentRow, CampaignDetail, CampaignRow, CampaignStatus, CampaignVenue, CohortPreview, CursorPage,
  JobRole, LaunchResult, Partner, Plant,
} from '@dru/types';
import { APPOINTMENT_STATUS_LABEL, CAMPAIGN_STATUS_LABEL } from '@dru/types';
import {
  AddressBlock, addressForm, addressPayload, Alert, Button, Card, ConfirmDialog, DataTable, Detail,
  DetailHeader, EntityCombobox, ErrorState, FilterBar, IconAction, InfoTip, KeyFigures, RowActions, SearchSelect, SelectField, StatusBadge, Steps, TextField, TextSkeleton,
  useIdempotencyKey, type AddressFormValue, type StepSpec, type Tone, cn,
} from '@dru/ui';
import { ChevronDown, Pencil, Trash2 } from 'lucide-react';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { ApiError } from '@dru/api-client';
import { api } from '../../../lib/api';
import { otherBranches } from '../../../lib/branches';
import { AddressFields } from '../../../components/AddressFields';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { isPlantBound } from '../lib/billing-view';
import { Dialog } from '../../../components/Dialog';
import { EXAM_TYPES, HospitalPicker, searchPhysicians } from '../components/ArrangeDialog';
import type { ExaminationOrderRow, ExamStatus, PhysicianRow, PartnerLocation } from '@dru/types';
import { EXAM_STATUS_LABEL } from '@dru/types';
import { usePages } from '../../../lib/usePages';


/**
 * Campaigns — the periodic drive that raises hundreds of examinations at once.
 *
 * Four acts, in order, and the order is the point:
 *
 *   draft     the filters, with the cohort counted but nothing created
 *   launch    the cohort is FROZEN and one order per worker is generated
 *   activate  consent opens and the workers are notified
 *   complete  refused while any order is still live
 *
 * The count before the launch is what makes this safe to use. An over-broad
 * filter caught on the draft screen is a corrected form; caught after launch
 * it is a thousand orders to cancel one at a time.
 */
function CampaignsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const setStatus = (v: string) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set('status', v); else n.delete('status');
    return n;
  }, { replace: true });
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();
  const { permissions } = useAuth();
  const canPlan = holds(permissions, 'campaign.create');

  const campaigns = useQuery<CampaignRow[]>(
    () => api.get(`/campaigns?limit=100${status ? `&status=${status}` : ''}`),
    [status],
  );
  const rows = campaigns.data ?? [];
  // The guide is open for a company that has never run one, folded after.
  const firstTime = !status && !campaigns.loading && !campaigns.error && rows.length === 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Campaigns"
        hint="A company-wide health checkup drive: one medical case per employee in the plants and job roles you choose, raised in one go."
        actions={canPlan && <Button onClick={() => setCreating(true)}>Plan a campaign</Button>}
      />

      <LifecycleGuide open={firstTime} />

      {/* One tap per state. The list endpoint filters by one status, so
          each chip is exactly one request. */}
      <div role="group" aria-label="Filter by state"
           className="flex gap-2 max-md:-mx-4 max-md:overflow-x-auto max-md:px-4 max-md:pb-1 md:flex-wrap">
        {STATUS_CHIPS.map(([value, label]) => {
          const on = status === value;
          return (
            <button
              key={value || 'all'} type="button" aria-pressed={on}
              onClick={() => setStatus(value)}
              className={cn(
                'inline-flex h-8 shrink-0 items-center rounded-full border px-3 text-small font-medium transition-colors max-md:h-10',
                on ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-neutral-300 bg-white text-neutral-700 hover:border-primary-300 hover:bg-primary-50',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      <DataTable<CampaignRow>
        rows={rows}
        loading={campaigns.loading}
        error={campaigns.error}
        onRetry={campaigns.reload}
        rowKey={(c) => c.id}
        onRowClick={(c) => nav(`/hr/campaigns/${c.id}`)}
        empty={{
          title: status ? 'No campaigns in this state' : 'No campaigns yet',
          hint: status
            ? 'Choose another state, or show all campaigns.'
            : 'Plan your first annual or periodic checkup drive. Nothing is raised until you launch it, and you see exactly who it covers first.',
          action: status
            ? <Button variant="secondary" onClick={() => setStatus('')}>Show all campaigns</Button>
            : canPlan ? <Button onClick={() => setCreating(true)}>Plan a campaign</Button> : undefined,
        }}
        columns={[
          {
            key: 'name', header: 'Campaign', alwaysVisible: true,
            cell: (c) => (
              <Link
                to={`/hr/campaigns/${c.id}`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-neutral-900 hover:text-primary-700"
              >
                {c.name}
              </Link>
            ),
          },
          {
            key: 'type', header: 'Checkup type',
            cell: (c) => <span className="text-small text-neutral-700">{EXAM_TYPE_LABEL[c.examinationType] ?? c.examinationType}</span>,
          },
          {
            key: 'dates', header: 'Dates',
            cell: (c) => (
              <span className="whitespace-nowrap text-small tabular-nums text-neutral-700">
                {formatDate(c.startsOn)} – {formatDate(c.endsOn)}
              </span>
            ),
          },
          {
            key: 'cohort', header: 'Employees', align: 'right',
            cell: (c) => (c.cohortSize == null
              ? <span className="text-small text-neutral-500">{c.status === 'CANCELLED' ? '—' : 'Counted at launch'}</span>
              : <span className="text-small tabular-nums text-neutral-800">{c.cohortSize.toLocaleString('en-IN')}</span>),
          },
          {
            key: 'status', header: 'Status',
            cell: (c) => (
              <StatusBadge tone={campaignTone(c.status)}>
                {CAMPAIGN_STATUS_LABEL[c.status] ?? c.status}
              </StatusBadge>
            ),
          },
          {
            key: 'next', header: 'Next action',
            cell: (c) => (
              <span className={cn('text-small', FINISHED.includes(c.status) ? 'text-neutral-500' : 'font-medium text-primary-700')}>
                {NEXT_STEP[c.status]}
              </span>
            ),
          },
        ]}
      />

      {creating && (
        <PlanDialog
          onClose={() => setCreating(false)}
          onDone={(id) => { setCreating(false); nav(`/hr/campaigns/${id}`); }}
        />
      )}
    </div>
  );
}

const STATUS_CHIPS: Array<[string, string]> = [
  ['', 'All'],
  ['DRAFT', 'Draft'],
  ['SCHEDULED', 'Launched'],
  ['ACTIVE', 'Active'],
  ['IN_PROGRESS', 'In progress'],
  ['COMPLETED', 'Completed'],
  ['CLOSED', 'Closed'],
  ['CANCELLED', 'Cancelled'],
];

/**
 * The five acts of a campaign, in plain words, mapped onto the backend's
 * states (DRAFT → SCHEDULED → ACTIVE → IN_PROGRESS → COMPLETED). Open for a
 * company that has never run one; one click away after that.
 */
const LIFECYCLE: Array<{ key: string; title: string; text: string }> = [
  { key: 'draft', title: 'Draft', text: 'Choose dates, plants and job roles. Nothing is raised yet.' },
  { key: 'launch', title: 'Launch', text: 'Who it covers is frozen and one medical case is raised per employee.' },
  { key: 'activate', title: 'Activate', text: 'Consent opens and every employee is notified.' },
  { key: 'track', title: 'Track', text: 'Employees are booked and examined. Follow up anyone missed.' },
  { key: 'complete', title: 'Complete', text: 'Once every case has an outcome, close the campaign.' },
];

function LifecycleGuide({ open }: { open: boolean }) {
  return (
    <details open={open || undefined} className="group rounded-card border border-neutral-200 bg-white shadow-card">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-small font-medium text-neutral-800 max-md:min-h-11 [&::-webkit-details-marker]:hidden">
        How a campaign works
        <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-neutral-500 transition-transform group-open:rotate-180" />
      </summary>
      <ol className="grid gap-3 border-t border-neutral-100 px-4 py-4 sm:grid-cols-2 lg:grid-cols-5">
        {LIFECYCLE.map((st, i) => (
          <li key={st.key} className="flex gap-3 lg:flex-col lg:gap-2">
            <span aria-hidden className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-50 text-small font-semibold text-primary-700">
              {i + 1}
            </span>
            <span className="min-w-0">
              <span className="block text-small font-semibold text-neutral-900">{st.title}</span>
              <span className="block text-small text-neutral-600">{st.text}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className="border-t border-neutral-100 px-4 py-3 text-caption text-neutral-500">
        The tests come from the medical package {BRAND.name} set up for your company. There is nothing medical to choose.
      </p>
    </details>
  );
}

/** What the campaign is waiting for, in one line, for the list. */
const NEXT_STEP: Record<CampaignStatus, string> = {
  DRAFT: 'Check who it covers, then launch',
  SCHEDULED: 'Activate: notify employees',
  ACTIVE: 'Book employees, follow up',
  IN_PROGRESS: 'Follow up open cases, then complete',
  COMPLETED: 'Nothing — finished',
  CLOSED: 'Nothing — closed',
  CANCELLED: 'Nothing — cancelled',
};

const FINISHED: CampaignStatus[] = ['COMPLETED', 'CLOSED', 'CANCELLED'];

function campaignTone(s: CampaignStatus): Tone {
  switch (s) {
    case 'COMPLETED': case 'CLOSED': return 'covered';
    case 'ACTIVE': case 'IN_PROGRESS': return 'pending';
    case 'SCHEDULED': return 'dueSoon';
    case 'CANCELLED': return 'neutral';
    default: return 'neutral';
  }
}

function PlanDialog({ existing, onClose, onDone }: {
  /** A draft to correct, rather than a new plan. */
  existing?: CampaignDetail | null;
  onClose: () => void; onDone: (id: string) => void;
}) {
  const { principal } = useAuth();
  const plants = useQuery<CursorPage<Plant>>(() => api.get('/plants?limit=100'), []);
  const jobRoles = useQuery<CursorPage<JobRole>>(() => api.get('/job-roles?limit=100'), []);

  const today = new Date().toISOString().slice(0, 10);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [form, setForm] = useState({
    name: existing?.name ?? '', examinationType: existing?.examinationType ?? 'PERIODIC_OHC',
    startsOn: existing?.startsOn.slice(0, 10) ?? today, endsOn: existing?.endsOn.slice(0, 10) ?? today,
    partnerId: existing?.assignedPartnerId ?? '',
  });
  const [plantIds, setPlantIds] = useState<string[]>(existing?.plantIds ?? []);
  const [jobRoleIds, setJobRoleIds] = useState<string[]>(existing?.jobRoleIds ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The draft's hospital, by name rather than id.
  const seeded = useQuery<Partner | null>(
    () => (existing?.assignedPartnerId ? api.get<Partner>(`/partners/${existing.assignedPartnerId}`) : Promise.resolve(null)),
    [existing?.assignedPartnerId],
  );
  useEffect(() => { if (seeded.data && !partner) setPartner(seeded.data); }, [seeded.data, partner]);

  const boundToOne = isPlantBound(principal) && (plants.data?.items.length ?? 0) === 1
    && !(existing && existing.plantIds.length === 0);

  async function submit() {
    setBusy(true); setError(null);
    try {
      const body = {
        name: form.name,
        examinationType: form.examinationType,
        startsOn: form.startsOn,
        endsOn: form.endsOn,
        // A plant-bound login with one plant: the picker was not shown and
        // the summary said "in <that plant>", so save exactly that ([] would
        // mean every plant, which it cannot launch). A company login only
        // sees one plant because of the header switcher, so it keeps [] =
        // company-wide, and an existing company-wide draft is never narrowed.
        plantIds: plantIds.length ? plantIds : (boundToOne ? [plants.data!.items[0]!.id] : []),
        jobRoleIds,
        partnerId: form.partnerId || null,
      };
      if (existing) {
        await api.patch(`/campaigns/${existing.id}`, body);
        onDone(existing.id);
      } else {
        const created = await api.post<{ id: string }>('/campaigns', {
          ...body,
          ...(form.partnerId ? { partnerId: form.partnerId } : { partnerId: undefined }),
        }, crypto.randomUUID());
        onDone(created.id);
      }
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  /**
   * Four steps, and they are the four that exist.
   *
   * The brief asks for nine. Three of those are not steps in this product and
   * a picker for them would be a lie:
   *
   *   · The TEST BATTERY is not chosen per campaign. It is derived from each
   *     worker's job role and its mapped hazards, which is what stops two
   *     people on the same furnace being screened differently. Step 4 below
   *     says so rather than offering a protocol dropdown that would create a
   *     second source of truth.
   *   · CONSENT and NOTIFICATIONS are not configured here either — activating
   *     the campaign opens consent and queues the messages, and the channel
   *     policy decides what may go over which channel.
   *   · SLOT ALLOCATION happens per worker at booking, against the venue's
   *     daily capacity. There is no bulk allocation to configure yet.
   *
   * Nothing is created until Save draft, and even then no examination exists:
   * the cohort is counted on the next screen and only launching raises them.
   */
  const STEPS = ['Details', 'Who it covers', 'Where', 'Review'] as const;
  const [step, setStep] = useState(0);

  const plantOptions = plants.data?.items ?? [];
  const roleOptions = jobRoles.data?.items ?? [];
  const namedPlants = plantOptions.filter((p) => plantIds.includes(p.id));
  const namedRoles = roleOptions.filter((r) => jobRoleIds.includes(r.id));
  // A one-plant company has nothing to pick; keep a draft's own ticks visible.
  const showPlantPicker = plantOptions.length > 1 || plantIds.length > 0;
  const showRolePicker = roleOptions.length > 0 || jobRoleIds.length > 0;
  const coverage = [
    'every active employee',
    namedPlants.length
      ? `in ${namedPlants.map((p) => p.name).join(', ')}`
      : boundToOne ? `in ${plantOptions[0]!.name}` : 'in every plant',
    namedRoles.length ? `with job role ${namedRoles.map((r) => r.title).join(', ')}` : '',
  ].filter(Boolean).join(' ') + '.';
  const detailsReady = form.name.trim().length >= 3 && form.endsOn >= form.startsOn;

  return (
    <Dialog title={existing ? `Edit ${existing.name}` : 'Plan a campaign'} onClose={onClose} wide>
      {/* The step rail doubles as the progress indicator. Completed steps stay
          clickable, because a review step you cannot go back from is a review
          step nobody trusts. */}
      <ol className="mt-1 grid grid-cols-4 gap-1" aria-label="Steps">
        {STEPS.map((label, i) => (
          <li key={label} className="min-w-0">
            <button
              type="button"
              onClick={() => { if (i < step || detailsReady) setStep(i); }}
              aria-current={i === step ? 'step' : undefined}
              disabled={i > step && !detailsReady}
              className="group flex w-full min-w-0 flex-col items-start gap-1.5 rounded-input py-1 text-left max-md:min-h-11 disabled:cursor-not-allowed"
            >
              <span aria-hidden className={cn(
                'block h-1 w-full rounded-full transition-colors',
                i <= step ? 'bg-primary-600' : 'bg-neutral-200',
              )} />
              <span className={cn(
                'block w-full truncate text-caption font-medium',
                i === step ? 'text-primary-700' : i < step ? 'text-neutral-700 group-hover:text-primary-700' : 'text-neutral-500',
              )}>
                <span className="sm:hidden">{i + 1}. </span><span className="max-sm:hidden">{i + 1}. {label}</span>
                <span className="sm:hidden">{i === step ? label : ''}</span>
              </span>
            </button>
          </li>
        ))}
      </ol>

      {/* ── 1 · Details ──────────────────────────────────────── */}
      {step === 0 && (
        <>
          <TextField
            label="Campaign name"
            required
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            autoFocus
            placeholder="Annual Health Checkup 2026"
            hint="At least three characters. Employees see this name in their app."
            className="mt-4"
          />

          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <SelectField
              label="Checkup type"
              value={form.examinationType}
              onChange={(v) => setForm({ ...form, examinationType: v })}
            >
              {EXAM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </SelectField>
            <TextField
              label="Opens" type="date" required value={form.startsOn}
              onChange={(v) => setForm({ ...form, startsOn: v })}
            />
            <TextField
              label="Closes" type="date" required value={form.endsOn}
              onChange={(v) => setForm({ ...form, endsOn: v })}
              error={form.endsOn < form.startsOn ? 'Must be on or after the opening date.' : null}
            />
          </div>
        </>
      )}

      {/* ── 2 · Who it covers ────────────────────────────────── */}
      {step === 1 && (
        <div className="mt-4 space-y-3">
          {/* Leaving a list empty means "no constraint on it", not "nobody".
              A drive across every plant is the common case. Departments are
              not offered: they stopped narrowing a cohort on 26 Oct 2026. */}
          <p className="rounded-input bg-neutral-50 px-3 py-2 text-small text-neutral-800">
            <span className="font-medium">Covers: </span>{coverage}
          </p>
          <div className={cn('grid gap-3', showPlantPicker && showRolePicker && 'sm:grid-cols-2')}>
            {showPlantPicker && (
              <Picker
                title="Plants"
                options={plantOptions.map((p) => ({ id: p.id, label: `${p.name} (${p.code})` }))}
                selected={plantIds}
                onToggle={(id) => setPlantIds((s) => toggle(s, id))}
              />
            )}
            {showRolePicker && (
              <Picker
                title="Job roles"
                options={roleOptions.map((r) => ({ id: r.id, label: r.title }))}
                selected={jobRoleIds}
                onToggle={(id) => setJobRoleIds((s) => toggle(s, id))}
              />
            )}
          </div>
          <p className="text-caption text-neutral-500">
            Leave a list with nothing ticked to include everyone. The exact number of employees is
            counted after you save, before anything is raised.
          </p>
        </div>
      )}

      {/* ── 3 · Where ────────────────────────────────────────── */}
      {step === 2 && (
        <div className="mt-4 space-y-4">
          {/* Optional, and it stays optional: clearing the picker means "decide
              per worker at routing", which is the sensible default for a
              campaign spanning plants that are nowhere near each other. */}
          <EntityCombobox<Partner>
            label="Hospital (optional)"
            value={partner}
            onChange={(p) => { setPartner(p); setForm((f) => ({ ...f, partnerId: p?.id ?? '' })); }}
            fetchOptions={async (search, cursor) => {
              const page = await api.get<CursorPage<Partner>>(
                `/partners?type=HOSPITAL&limit=20&q=${encodeURIComponent(search)}`
                + (cursor ? `&cursor=${cursor}` : ''),
              );
              return { items: page.items, nextCursor: page.nextCursor };
            }}
            getKey={(p) => p.id}
            placeholder="Decide per employee at routing"
            searchPlaceholder="Hospital name or code"
            emptyMessage="No empanelled hospital matches that."
            info="Routing still checks each employee's protocol against what the venue can run, and refuses one that cannot."
            renderOption={(p) => (
            <>
              <span className="block font-medium text-neutral-900">{p.name}</span>
              <span className="block text-caption font-normal text-neutral-500">
                {[
                  [p.locality, p.city].filter(Boolean).join(', ') || p.city,
                  `${p.testsOffered} tests`,
                  `${p.dailyCapacity}/day`,
                ].filter(Boolean).join(' · ')}
              </span>
            </>
          )}
            renderValue={(p) => `${p.name}${p.city ? ` — ${p.city}` : ''}`}
          />

          <Alert tone="info" title="No tests to choose">
            Each employee's tests come from the medical package {BRAND.name} set up for your
            company. You can add camp venues (a plant's medical room, say) after saving.
          </Alert>
        </div>
      )}

      {/* ── 4 · Review ───────────────────────────────────────── */}
      {step === 3 && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-x-8 gap-y-3 rounded-card border border-neutral-200 p-4 sm:grid-cols-2">
            <Detail label="Name" value={form.name} />
            <Detail
              label="Checkup type"
              value={EXAM_TYPE_LABEL[form.examinationType] ?? form.examinationType}
            />
            <Detail label="Opens" value={formatDate(form.startsOn)} />
            <Detail label="Closes" value={formatDate(form.endsOn)} />
            <Detail
              label="Plants"
              value={namedPlants.length
                ? namedPlants.map((p) => p.name).join(', ')
                : plantOptions.length === 1 ? plantOptions[0]!.name : 'Every plant'}
            />
            <Detail
              label="Job roles"
              value={namedRoles.length
                ? namedRoles.map((r) => r.title).join(', ')
                : 'Every job role'}
            />
            <Detail label="Hospital" value={partner?.name ?? 'Decided per employee at routing'} />
          </div>

          <div className="rounded-card border border-primary-200 bg-primary-50 p-4">
            <h3 className="text-small font-semibold text-neutral-900">What happens when you save</h3>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-small text-neutral-700">
              <li>A <strong>draft</strong> is saved. No medical case is raised and nobody is notified.</li>
              <li>The next screen shows exactly which employees it covers, and anyone it will skip.</li>
              <li>Only when you <strong>launch</strong> is that list frozen and one case raised per employee.</li>
            </ol>
          </div>
        </div>
      )}

      {error && <Alert>{error}</Alert>}

      <div className="mt-5 flex justify-between gap-2">
        <Button
          variant="secondary"
          onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
          disabled={busy}
        >
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>

        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!detailsReady}>
            Next
          </Button>
        ) : (
          <Button onClick={submit} loading={busy} disabled={!detailsReady}>
            {existing ? 'Save changes' : 'Save draft'}
          </Button>
        )}
      </div>
    </Dialog>
  );
}

function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function Picker({ title, options, selected, onToggle }: {
  title: string;
  options: Array<{ id: string; label: string }>;
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-caption font-semibold uppercase tracking-wide text-neutral-600">
          {title}
        </span>
        <span className="text-caption text-neutral-500">
          {selected.length ? `${selected.length} chosen` : 'Any'}
        </span>
      </div>
      <div
        role="group"
        aria-label={title}
        className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-input border border-neutral-200 p-2"
      >
        {options.map((o) => (
          <label key={o.id} className="flex min-h-6 items-start gap-2 text-small">
            <input
              type="checkbox"
              checked={selected.includes(o.id)}
              onChange={() => onToggle(o.id)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="min-w-0 break-words text-neutral-800">{o.label}</span>
          </label>
        ))}
        {!options.length && <p className="text-caption text-neutral-500">Nothing to choose from yet</p>}
      </div>
    </div>
  );
}

/* ── Venues ─────────────────────────────────────────────────── */

/**
 * Where the campaign is run. One campaign, several venues — a company with
 * three plants screens each on its own week at its own gate. A venue at a
 * plant starts from the plant's address and keeps its own copy, because
 * "the main-gate medical room" is not the plant's registered address.
 */
function Venues({ campaignId, editable, plants }: { campaignId: string; editable: boolean; plants: Plant[] }) {
  const venues = useQuery<{ items: CampaignVenue[] }>(() => api.get(`/campaigns/${campaignId}/venues`), [campaignId]);
  const [dialog, setDialog] = useState<null | 'new' | CampaignVenue>(null);
  const [error, setError] = useState<string | null>(null);

  const [removing, setRemoving] = useState<CampaignVenue | null>(null);
  async function remove(v: CampaignVenue) {
    setError(null);
    try { await api.del(`/campaigns/${campaignId}/venues/${v.id}`); venues.reload(); }
    catch (e) { setError(errorMessage(e)); }
  }

  return (
    <section className="rounded-card border border-neutral-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">
            Where employees go{venues.data ? ` (${venues.data.items.length})` : ''}
          </h2>
          <InfoTip label="About where employees go">
            A booking made under this campaign can name one of these venues, and the employee's app
            shows its address, dates and contact with directions.
          </InfoTip>
        </div>
        {editable && <Button size="sm" variant="secondary" onClick={() => setDialog('new')}>Add a venue</Button>}
      </div>
      {error && <Alert className="mt-2">{error}</Alert>}
      {venues.error && <Alert className="mt-2">{venues.error}</Alert>}
      {venues.loading ? <TextSkeleton lines={2} className="mt-2" /> : (venues.data?.items.length ?? 0) === 0 ? (
        <p className="mt-2 text-small text-neutral-700">
          No camp venue. Employees are booked at the hospital's own address.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-neutral-100">
          {venues.data!.items.map((v) => (
            <li key={v.id} className="flex items-start justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="text-body font-medium text-neutral-900">{v.name}</div>
                <AddressBlock address={v} className="mt-0.5 text-small" empty="No address" />
                <div className="mt-0.5 text-caption text-neutral-500">
                  {[v.startsOn ? `${formatDate(v.startsOn)}${v.endsOn ? ` → ${formatDate(v.endsOn)}` : ''}` : null,
                    v.contactName, v.contactPhone].filter(Boolean).join(' · ')}
                  {v.appointments ? ` · ${v.appointments} booked` : ''}
                </div>
              </div>
              {editable && (
                <RowActions className="shrink-0">
                  <IconAction label={`Edit ${v.name}`} icon={Pencil} onClick={() => setDialog(v)} />
                  <IconAction label={`Remove ${v.name}`} icon={Trash2} danger onClick={() => setRemoving(v)} />
                </RowActions>
              )}
            </li>
          ))}
        </ul>
      )}
      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.name}?`}
          what="The venue is taken off this campaign."
          affects={removing.appointments ? `${removing.appointments} booking${removing.appointments === 1 ? '' : 's'} name this venue and will keep the address as it was.` : 'Nothing is booked here yet.'}
          confirmLabel="Remove venue"
          tone="danger"
          onConfirm={async () => { await remove(removing); setRemoving(null); }}
          onCancel={() => setRemoving(null)}
        />
      )}
      {dialog && (
        <VenueDialog campaignId={campaignId} venue={dialog === 'new' ? null : dialog} plants={plants}
          onClose={() => setDialog(null)} onDone={() => { setDialog(null); venues.reload(); }} />
      )}
    </section>
  );
}

function VenueDialog({ campaignId, venue, plants, onClose, onDone }: {
  campaignId: string; venue: CampaignVenue | null; plants: Plant[]; onClose: () => void; onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: venue?.name ?? '', plantId: venue?.plantId ?? '',
    contactName: venue?.contactName ?? '', contactPhone: venue?.contactPhone ?? '',
    startsOn: venue?.startsOn?.slice(0, 10) ?? '', endsOn: venue?.endsOn?.slice(0, 10) ?? '',
    notes: venue?.notes ?? '',
  });
  const [address, setAddress] = useState<AddressFormValue>(addressForm(venue));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const idem = useIdempotencyKey();
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Choosing a plant copies its address in, once, as a starting point.
  function choosePlant(id: string) {
    set('plantId')(id);
    const p = plants.find((x) => x.id === id);
    if (p && !address.addressLine1 && !address.pincode) setAddress(addressForm(p));
  }

  async function save() {
    setBusy(true); setError(null); setFieldErrors({});
    const body = {
      name: form.name.trim(),
      plantId: form.plantId || null,
      ...addressPayload(address),
      ...(form.contactName ? { contactName: form.contactName } : {}),
      ...(form.contactPhone ? { contactPhone: form.contactPhone } : {}),
      ...(form.startsOn ? { startsOn: form.startsOn } : {}),
      ...(form.endsOn ? { endsOn: form.endsOn } : {}),
      ...(form.notes ? { notes: form.notes } : {}),
      copyPlantAddress: false,
    };
    try {
      if (venue) await api.patch(`/campaigns/${campaignId}/venues/${venue.id}`, body);
      else { await api.post(`/campaigns/${campaignId}/venues`, body, idem.key()); idem.rotate(); }
      onDone();
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title={venue ? `Edit ${venue.name}` : 'Add a venue'} onClose={onClose} wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Venue name" value={form.name} onChange={set('name')} autoFocus error={fieldErrors.name}
          placeholder="Vapi Plant — Main Gate Medical Room" className="sm:col-span-2" />
        <SearchSelect label="At a plant" value={form.plantId} onChange={choosePlant} hint="Copies the plant's address in as a starting point."
          placeholder="Not at a plant" searchPlaceholder="Search plants by name or code…"
          options={[{ value: '', label: 'Not at a plant' }, ...plants.map((p) => ({ value: p.id, label: p.name, hint: p.code }))]} />
        <div />
        <TextField label="First day" value={form.startsOn} onChange={set('startsOn')} type="date" error={fieldErrors.startsOn} />
        <TextField label="Last day" value={form.endsOn} onChange={set('endsOn')} type="date" error={fieldErrors.endsOn} />
        <TextField label="Contact at the venue" value={form.contactName} onChange={set('contactName')} error={fieldErrors.contactName} />
        <TextField label="Phone" value={form.contactPhone} mono error={fieldErrors.contactPhone}
          onChange={(v) => set('contactPhone')(v.replace(/\D/g, '').slice(0, 10))} />
      </div>
      <div className="mt-4">
        <h3 className="text-caption font-semibold uppercase tracking-wide text-neutral-700">Where it is</h3>
        <div className="mt-2"><AddressFields value={address} onChange={setAddress} errors={fieldErrors} /></div>
      </div>
      <TextField label="Notes for the employee" value={form.notes} onChange={set('notes')} placeholder="Carry a photo ID. Enter through Gate 2." />
      {error && <Alert>{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={form.name.trim().length < 2}>{venue ? 'Save' : 'Add venue'}</Button>
      </div>
    </Dialog>
  );
}

/**
 * Where the campaign is on its five acts, the same five the list page's
 * guide explains. Backend states: DRAFT → SCHEDULED (launched) → ACTIVE
 * (consent open) → IN_PROGRESS (examinations under way) → COMPLETED/CLOSED.
 * A cancelled campaign stops on the act it reached, drawn blocked.
 */
function campaignSteps(c: CampaignDetail): StepSpec[] {
  const at = c.status === 'DRAFT' ? 0
    : c.status === 'SCHEDULED' ? 2
    : c.status === 'ACTIVE' || c.status === 'IN_PROGRESS' ? 3
    : c.status === 'CANCELLED' ? (c.launchedAt ? 2 : 1)
    : 5;
  const state = (i: number): StepSpec['state'] => {
    if (i < at) return 'done';
    if (i === at) return c.status === 'CANCELLED' ? 'blocked' : 'current';
    return 'upcoming';
  };
  const steps: StepSpec[] = [
    { key: 'draft', label: 'Draft', state: state(0), detail: c.status === 'DRAFT' ? 'Check who it covers' : undefined },
    { key: 'launch', label: 'Launch', state: state(1), detail: c.launchedAt ? `Cases raised ${formatDate(c.launchedAt)}` : undefined },
    { key: 'activate', label: 'Activate', state: state(2), detail: at > 2 && c.status !== 'CANCELLED' ? 'Employees notified' : undefined },
    { key: 'track', label: 'Track', state: state(3), detail: c.progress.total ? `${c.progress.certified} of ${c.progress.total} signed off` : undefined },
    { key: 'complete', label: 'Complete', state: state(4), detail: c.completedAt ? formatDate(c.completedAt) : undefined },
  ];
  return steps.map((st) => (c.status === 'CANCELLED' && st.state === 'blocked' ? { ...st, detail: 'Cancelled here' } : st));
}

function CampaignDetailPagePage() {
  const { permissions } = useAuth();
  const { id = '' } = useParams();
  const campaign = useQuery<CampaignDetail>(() => api.get(`/campaigns/${id}`), [id]);
  const cohort = useQuery<CohortPreview>(() => api.get(`/campaigns/${id}/cohort`), [id]);
  const plants = useQuery<CursorPage<Plant>>(() => api.get('/plants?limit=100'), []);
  const canSeeBookings = holds(permissions, 'appointment.read');
  const canSeeCases = holds(permissions, 'examination.read');
  const appointments = useQuery<CursorPage<AppointmentRow> | null>(
    () => (canSeeBookings ? api.get<CursorPage<AppointmentRow>>(`/appointments?campaignId=${id}&limit=100`) : Promise.resolve(null)),
    [id, canSeeBookings]);
  const [launched, setLaunched] = useState<LaunchResult | null>(null);
  const [confirming, setConfirming] = useState<null | 'launch' | 'activate' | 'complete' | 'cancel'>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [arranging, setArranging] = useState(false);
  const [ordersNonce, setOrdersNonce] = useState(0);
  const [orderStatus, setOrderStatus] = useState('');

  const c = campaign.data;
  const can = (p: string) => holds(permissions, p);
  const size = cohort.data?.cohortSize ?? 0;
  // Workers the launch passes over because a case is already open for them.
  const skipping = cohort.data?.alreadyOpen ?? 0;
  const raising = Math.max(0, size - skipping);
  // Cancelled before launch: nobody was ever covered, so no "if launched now".
  const neverLaunched = c?.status === 'CANCELLED' && !c.launchedAt;

  /** Throws, so the confirmation that called it stays open with the reason. */
  async function act(path: string, body?: unknown) {
    setError(null);
    const res = await api.post(`/campaigns/${id}/${path}`, body ?? {});
    if (path === 'launch') setLaunched(res as LaunchResult);
    campaign.reload();
    cohort.reload();
    setOrdersNonce((n) => n + 1);
  }

  /** The one thing the campaign is waiting for, and who may do it. */
  const next = (() => {
    if (!c) return null;
    switch (c.status) {
      case 'DRAFT':
        return {
          text: size
            ? `Check the ${size} employees below. Launching freezes this list and raises one medical case for each.`
            : 'Nobody matches the plan yet. Edit the plants or job roles until the cohort is right.',
          action: can('campaign.launch') ? (
            <Button disabled={!raising} onClick={() => setConfirming('launch')}>
              Launch — raise {raising} medical case{raising === 1 ? '' : 's'}
            </Button>
          ) : null,
          who: 'HR launches the campaign (needs campaign launch rights).',
        };
      case 'SCHEDULED':
        return {
          text: 'Medical cases are raised. Activating notifies every employee and asks for their consent to the checkup.',
          action: can('campaign.update') ? (
            <Button onClick={() => setConfirming('activate')}>Activate — notify employees</Button>
          ) : null,
          who: 'HR opens consent (needs campaign edit rights).',
        };
      case 'ACTIVE':
      case 'IN_PROGRESS':
        return c.progress.openOrderCount > 0 ? {
          text: can('examination.route')
            ? `${c.progress.openOrderCount} order${c.progress.openOrderCount === 1 ? ' is' : 's are'} still open. Route and book them, then follow up anyone missed.`
            : `${c.progress.openOrderCount} order${c.progress.openOrderCount === 1 ? ' is' : 's are'} still open. ${BRAND.name} Operations assigns the lab and books the cohort; follow up anyone missed from the orders below.`,
          action: can('examination.route')
            ? <Button onClick={() => setArranging(true)}>Route and book the cohort</Button>
            : null,
          who: null,
        } : {
          text: 'Every order has reached an outcome. Complete the campaign to close it.',
          action: can('campaign.update')
            ? <Button onClick={() => setConfirming('complete')}>Complete the campaign</Button>
            : null,
          who: 'HR completes the campaign (needs campaign edit rights).',
        };
      case 'CANCELLED':
        return { text: `Cancelled${c.cancelReason ? ` — “${c.cancelReason}”` : ''}. Plan a new campaign to cover the same people.`, action: null, who: null };
      default:
        return { text: 'This campaign is finished. Certificates it produced are on each employee’s record.', action: null, who: null };
    }
  })();

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Campaign"
        title={c?.name ?? (campaign.error ? 'Campaign' : 'Loading…')}
        badges={c && <StatusBadge tone={campaignTone(c.status)}>{CAMPAIGN_STATUS_LABEL[c.status] ?? c.status}</StatusBadge>}
        subtitle={c && [`${formatDate(c.startsOn)} – ${formatDate(c.endsOn)}`, (EXAM_TYPE_LABEL[c.examinationType] ?? c.examinationType)].join(' · ')}
        actions={c?.status === 'DRAFT' && can('campaign.create') && (
          <Button variant="secondary" onClick={() => setEditing(true)}>Edit the plan</Button>
        )}
      />
      {campaign.loading && !c && <TextSkeleton lines={4} />}
      {campaign.error && (
        <ErrorState
          title="This campaign is not available"
          detail={`${campaign.error.replace(/\.$/, "")}. It may cover a plant outside your access, or it may have been removed.`}
          onRetry={campaign.reload}
        />
      )}

      {c && (
        <div className="space-y-4">
          <Card className="p-4">
            <Steps steps={campaignSteps(c)} label="Campaign progress" />
          </Card>

          {launched && (
            <Alert tone={launched.skipped.length ? 'warning' : 'success'} title={`${launched.generated} examination${launched.generated === 1 ? '' : 's'} raised`}>
              {launched.skipped.length > 0 && (
                <div className="mt-1 text-neutral-700">
                  {/* Not buried. Each one is somebody who still owes a
                      statutory examination and now has no order for it. */}
                  <p className="font-medium text-overdue-600">
                    {launched.skipped.length} employee{launched.skipped.length === 1 ? '' : 's'} could not be covered:
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {launched.skipped.slice(0, 10).map((s) => (
                      <li key={s.employeeCode}>
                        <span className="font-mono">{s.employeeCode}</span> — {s.reason}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-neutral-600">
                    Usually no medical package is set up for them. Ask {BRAND.name} support, then
                    schedule those checkups one by one from Medical cases.
                  </p>
                </div>
              )}
            </Alert>
          )}

          {next && (
            <section
              aria-label="Next step"
              className={cn(
                'rounded-card border bg-white p-4 shadow-card',
                c.status === 'CANCELLED' ? 'border-neutral-200' : 'border-primary-200',
              )}
            >
              <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Next step</h2>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <p className="min-w-0 flex-[1_1_20rem] text-body text-neutral-800">{next.text}</p>
                {next.action ?? (next.who && <span className="text-small text-neutral-500">{next.who}</span>)}
              </div>
            </section>
          )}

          {error && <Alert>{error}</Alert>}

          <KeyFigures
            label="Campaign figures"
            figures={[
              !neverLaunched && {
                key: 'cohort', label: c.cohortSize != null ? 'Employees (frozen)' : 'Employees if launched now',
                value: c.cohortSize ?? size, loading: cohort.loading && c.cohortSize == null,
              },
              // Signed off / open / missed live in the Progress card below.
              c.progress.total > 0 && { key: 'raised', label: 'Medical cases raised', value: c.progress.total },
              canSeeBookings && !!c.launchedAt && {
                key: 'booked', label: 'Appointments',
                value: (appointments.data?.items.length ?? 0) >= 100 ? '100+' : appointments.data?.items.length ?? 0,
                loading: appointments.loading,
              },
            ]}
          />

          {c.progress.total > 0 && (
            <CampaignProgressCard
              progress={c.progress}
              canSeeCases={canSeeCases}
              onShow={(st) => {
                setOrderStatus(st);
                document.getElementById('campaign-cases')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          )}

          <section className="rounded-card border border-neutral-200 bg-white p-4">
            <div className="flex items-center gap-1">
              <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">
                Who it covers
              </h2>
              <InfoTip label="About who it covers">
                Each employee’s tests come from the medical package {BRAND.name} set up for your company.
              </InfoTip>
            </div>
            <p className="mt-1 text-small text-neutral-600">
              {[
                c.plantIds.length ? `${c.plantIds.length} plant${c.plantIds.length === 1 ? '' : 's'}` : 'Every plant',
                c.jobRoleIds.length ? `${c.jobRoleIds.length} job role${c.jobRoleIds.length === 1 ? '' : 's'}` : 'every job role',
              ].join(' · ')}
              .
            </p>
            {cohort.loading && <TextSkeleton lines={2} className="mt-2" />}
            {cohort.error && <Alert className="mt-2">{cohort.error}</Alert>}
            {neverLaunched && (
              <p className="mt-2 text-body text-neutral-700">Cancelled before launch. Nobody was covered and no medical case was raised.</p>
            )}
            {cohort.data && !neverLaunched && (
              <>
                <p className="mt-2 text-body text-neutral-900">
                  <strong className="tabular-nums">{cohort.data.cohortSize}</strong>{' '}
                  employee{cohort.data.cohortSize === 1 ? '' : 's'}
                  {cohort.data.frozen
                    ? ` — frozen at launch${c.launchedAt ? ` on ${formatDate(c.launchedAt)}` : ''}`
                    : ' would be covered if this launched now'}
                </p>
                {!cohort.data.frozen && (
                  // The whole reason the count comes before the launch.
                  <p className="mt-1 flex items-center gap-1 text-small text-neutral-500">
                    Check this before launching.
                    <InfoTip label="About checking the cohort">
                      An over-broad filter is a corrected form now, and a thousand orders to cancel after.
                    </InfoTip>
                  </p>
                )}
                {(cohort.data as CohortPreview & { partialToYourPlants?: boolean }).partialToYourPlants && (
                  <p className="mt-1 text-small text-neutral-600">
                    This count covers only the plants you can see. The campaign reaches more.
                  </p>
                )}
                {!cohort.data.frozen && cohort.data.cohortSize === 0 && (
                  <Alert tone="warning" className="mt-2">
                    Nobody matches. Edit the plan: tick fewer job roles, or a different plant.
                  </Alert>
                )}
                {!cohort.data.frozen && skipping > 0 && (
                  // The launch passes these over; the number it raises is
                  // smaller than the cohort, and zero is refused outright.
                  <Alert tone="warning" className="mt-2">
                    {skipping === cohort.data.cohortSize
                      ? `Every employee in this cohort already has a medical case open, so launching would raise nothing and is refused. Finish or cancel those cases, or change the cohort.`
                      : `${skipping} of them already ${skipping === 1 ? 'has' : 'have'} a medical case open and will be skipped — ${raising} case${raising === 1 ? '' : 's'} would be raised.`}
                  </Alert>
                )}
                {cohort.data.sample.length > 0 && (
                  <ul aria-label="Employees in the cohort" className="mt-2 flex flex-wrap gap-1">
                    {cohort.data.sample.map((s) => (
                      <li key={s.employeeCode}>
                        <StatusBadge tone="neutral">{s.fullName} <span className="font-mono text-neutral-500">{s.employeeCode}</span></StatusBadge>
                      </li>
                    ))}
                    {cohort.data.cohortSize > cohort.data.sample.length && (
                      <li className="self-center text-caption text-neutral-500">
                        and {cohort.data.cohortSize - cohort.data.sample.length} more
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}
          </section>

          <Venues
            campaignId={c.id}
            editable={!FINISHED.includes(c.status) && can('campaign.update')}
            plants={plants.data?.items ?? []}
          />

          {c.progress.total > 0 && canSeeCases && (
            <CampaignOrders campaignId={c.id} nonce={ordersNonce} status={orderStatus} onStatus={setOrderStatus} />
          )}
          {canSeeBookings && c.launchedAt && <CampaignAppointments rows={appointments} />}

          {/* Kept apart from the next step: a destructive act placed beside
              the button somebody is about to press is how the wrong one
              gets pressed. */}
          {!FINISHED.includes(c.status) && can('campaign.cancel') && (
            <section className="rounded-card border border-danger-600/25 bg-white p-4">
              <h2 className="text-caption font-semibold uppercase tracking-wider text-danger-600">Cancel this campaign</h2>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <p className="min-w-0 flex-[1_1_20rem] text-small text-neutral-600">
                  Stops the campaign and closes its open orders. Employees already examined keep their
                  results and certificates. A cancelled campaign cannot be resumed.
                </p>
                <Button variant="danger" onClick={() => setConfirming('cancel')}>Cancel campaign</Button>
              </div>
            </section>
          )}
        </div>
      )}

      {confirming === 'launch' && c && (
        <ConfirmDialog
          title={`Launch ${c.name}?`}
          what={skipping
            ? `Who it covers is frozen and one medical case is raised for each of ${raising} employees. ${skipping} already ${skipping === 1 ? 'has' : 'have'} a medical case open and ${skipping === 1 ? 'is' : 'are'} skipped.`
            : `Who it covers is frozen and one medical case is raised for each of the ${size} employees.`}
          affects={`${raising} employee${raising === 1 ? '' : 's'} · ${formatDate(c.startsOn)} – ${formatDate(c.endsOn)}`}
          irreversible="Who it covers cannot be changed after launch. Raised cases are cancelled one at a time, or by cancelling the whole campaign."
          confirmLabel={`Launch and raise ${raising}`}
          onConfirm={async () => { await act('launch'); setConfirming(null); }}
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === 'activate' && c && (
        <ConfirmDialog
          title="Activate: open consent and notify employees?"
          what="Every employee in the cohort is sent a notification asking for consent to the examination."
          affects={`${c.cohortSize ?? size} employee${(c.cohortSize ?? size) === 1 ? '' : 's'}`}
          irreversible="Notifications cannot be recalled once sent."
          confirmLabel="Open consent and notify"
          onConfirm={async () => { await act('activate'); setConfirming(null); }}
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === 'complete' && c && (
        <ConfirmDialog
          title={`Complete ${c.name}?`}
          what="The campaign is marked finished. Its orders and certificates are kept as they are."
          affects={`${c.progress.certified} certified of ${c.progress.total} raised`}
          confirmLabel="Complete campaign"
          tone="success"
          onConfirm={async () => { await act('complete'); setConfirming(null); }}
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === 'cancel' && c && (
        <ConfirmDialog
          title={`Cancel ${c.name}?`}
          what="The campaign stops. Orders it raised that are still open are closed; employees already examined keep their results and certificates."
          affects={c.progress.total > 0 ? `${c.progress.openOrderCount} open order${c.progress.openOrderCount === 1 ? '' : 's'} out of ${c.progress.total}` : 'No examinations have been raised yet.'}
          irreversible="A cancelled campaign is not resumed. Plan a new one to cover the same people."
          confirmLabel="Cancel campaign"
          tone="danger"
          requireReason
          reasonHint="At least five characters. Recorded on the campaign and in the audit trail."
          onConfirm={async (reason) => { await act('cancel', { reason }); setConfirming(null); }}
          onCancel={() => setConfirming(null)}
        />
      )}
      {editing && c && (
        <PlanDialog existing={c} onClose={() => setEditing(false)}
          onDone={() => { setEditing(false); campaign.reload(); cohort.reload(); }} />
      )}
      {arranging && c && (
        <ArrangeCohortDialog campaign={c} onClose={() => setArranging(false)}
          onDone={() => { setArranging(false); campaign.reload(); appointments.reload(); setOrdersNonce((n) => n + 1); }} />
      )}
    </div>
  );
}

/**
 * How far the drive has got, from the campaign's own server counts (never
 * from a page of rows), and the employee follow-ups waiting on somebody.
 */
function CampaignProgressCard({ progress, canSeeCases, onShow }: {
  progress: CampaignDetail['progress'];
  canSeeCases: boolean;
  onShow: (status: string) => void;
}) {
  const pct = progress.total ? Math.round((progress.certified / progress.total) * 100) : 0;
  const closedOther = Math.max(0, progress.total - progress.certified - progress.openOrderCount);
  const pending: Array<{ key: string; text: string; status: string; tone: 'overdue' | 'pending' }> = [];
  if (progress.openNoShowCount > 0) {
    pending.push({ key: 'missed', status: 'NO_SHOW_OPEN', tone: 'overdue',
      text: `${progress.openNoShowCount} missed their appointment — rebook` });
  }
  const stillOpen = progress.openOrderCount - progress.openNoShowCount;
  if (stillOpen > 0) {
    pending.push({ key: 'open', status: '', tone: 'pending',
      text: `${stillOpen} still being booked or examined` });
  }
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Progress</h2>
        <span className="text-small text-neutral-600">
          <strong className="tabular-nums text-neutral-900">{progress.certified.toLocaleString('en-IN')}</strong>
          {' '}of {progress.total.toLocaleString('en-IN')} signed off · {pct}%
        </span>
      </div>
      <div
        className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-neutral-100"
        role="img"
        aria-label={`${progress.certified} signed off, ${progress.openOrderCount} open, ${closedOther} closed without sign-off, of ${progress.total}`}
      >
        <span className="chart-grow-x block h-full bg-covered-600" style={{ width: `${pct}%` }} />
        <span className="block h-full bg-primary-400" style={{ width: `${progress.total ? (progress.openOrderCount / progress.total) * 100 : 0}%` }} />
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-caption text-neutral-600">
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full bg-covered-600" />Signed off</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full bg-primary-400" />Open</span>
        {closedOther > 0 && <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2 w-2 rounded-full bg-neutral-300" />Closed without sign-off ({closedOther})</span>}
      </div>
      {pending.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-neutral-100 pt-3">
          {pending.map((p) => (
            <li key={p.key}>
              {canSeeCases ? (
                <button type="button" onClick={() => onShow(p.status)}
                        className={cn('text-left text-small font-medium hover:underline max-md:min-h-10',
                          p.tone === 'overdue' ? 'text-overdue-600' : 'text-primary-700')}>
                  {p.text} →
                </button>
              ) : (
                <span className={cn('text-small', p.tone === 'overdue' ? 'text-overdue-600' : 'text-neutral-700')}>{p.text}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Every order the campaign raised and where each has reached — the table
 * somebody running a drive reads every morning. "Missed 12" is a number;
 * this is the twelve.
 */
function CampaignOrders({ campaignId, nonce, status, onStatus: setStatus }: {
  campaignId: string; nonce: number; status: string; onStatus: (s: string) => void;
}) {
  const nav = useNavigate();
  const rows = usePages<ExaminationOrderRow>(
    (cursor) => api.get<CursorPage<ExaminationOrderRow>>(
      `/examinations?campaignId=${campaignId}&limit=50${status ? `&status=${status}` : ''}${cursor ? `&cursor=${cursor}` : ''}`),
    [campaignId, status, nonce],
  );
  const tone = (s: ExamStatus): Tone =>
    s === 'CERTIFIED' ? 'covered'
    : s === 'NO_SHOW_OPEN' || s === 'RETEST_REQUIRED' ? 'overdue'
    : ['CANCELLED', 'EXPIRED', 'NO_SHOW_CLOSED'].includes(s) ? 'neutral'
    : s === 'REQUESTED' || s === 'ROUTED' || s === 'CONSENT_PENDING' ? 'dueSoon'
    : 'pending';
  return (
    <section id="campaign-cases" className="scroll-mt-20 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">Medical cases raised</h2>
        <FilterBar
          filters={[{
            key: 'status', label: 'Case state', allLabel: 'Any state',
            options: (['REQUESTED', 'ROUTED', 'CONSENT_PENDING', 'SCHEDULED', 'NO_SHOW_OPEN', 'CHECKED_IN', 'IN_PROGRESS', 'LAB_PENDING', 'REVIEW_PENDING', 'RETEST_REQUIRED', 'CERTIFIED', 'CANCELLED'] as ExamStatus[])
              .map((st) => ({ value: st, label: EXAM_STATUS_LABEL[st] })),
          }]}
          value={{ status: status || undefined }}
          onChange={(v) => setStatus(v.status ?? '')}
        />
      </div>
      <DataTable<ExaminationOrderRow>
        rows={rows.items} loading={rows.loading} error={rows.error} onRetry={rows.reload}
        hasMore={rows.hasMore} onLoadMore={rows.loadMore} loadingMore={rows.loadingMore}
        rowKey={(o) => o.id}
        onRowClick={(o) => nav(`/hr/examinations/${o.id}`)}
        empty={{ title: 'No cases in this state', hint: 'Choose another state, or clear the filter.' }}
        columns={[
          { key: 'who', header: 'Employee', alwaysVisible: true, cell: (o) => (
            <div>
              <Link to={`/hr/examinations/${o.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-neutral-900 hover:text-primary-700">
                {o.employee?.fullName ?? '—'}
              </Link>
              <div className="font-mono text-caption text-neutral-500">{o.employee?.employeeCode}</div>
            </div>
          ) },
          { key: 'status', header: 'State', cell: (o) => <StatusBadge tone={tone(o.status)}>{EXAM_STATUS_LABEL[o.status] ?? o.status}</StatusBadge> },
          { key: 'due', header: 'Due', align: 'right', cell: (o) => <span className="text-small tabular-nums text-neutral-600">{formatDate(o.dueDate)}</span> },
        ]}
      />
    </section>
  );
}

/**
 * Route and book every order still at HR's step, against one hospital's
 * real daily capacity. What a campaign of five hundred used to cost in
 * dialogs, this costs in one; what it cannot cover it names.
 */
function ArrangeCohortDialog({ campaign, onClose, onDone }: {
  campaign: CampaignDetail; onClose: () => void; onDone: () => void;
}) {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [physician, setPhysician] = useState<PhysicianRow | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(campaign.startsOn.slice(0, 10) < today ? today : campaign.startsOn.slice(0, 10));
  const [to, setTo] = useState(campaign.endsOn.slice(0, 10));
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [slotMinutes, setSlotMinutes] = useState('30');
  const [skipSundays, setSkipSundays] = useState(true);
  const [locationId, setLocationId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    considered: number; routed: number; booked: number;
    skipped: Array<{ employeeCode: string; reason: string }>;
    bookedFor: Array<{ employeeCode: string; scheduledFor: string }>;
    partner: { name: string };
  } | null>(null);
  const idem = useIdempotencyKey();
  const partnerId = partner?.id ?? '';

  const seeded = useQuery<Partner | null>(
    () => (campaign.assignedPartnerId ? api.get<Partner>(`/partners/${campaign.assignedPartnerId}`) : Promise.resolve(null)),
    [campaign.assignedPartnerId],
  );
  useEffect(() => { if (seeded.data && !partner) setPartner(seeded.data); }, [seeded.data, partner]);
  useEffect(() => {
    if (!partnerId || physician) return;
    try { const raw = localStorage.getItem(`dru.hr.physicianFor.${partnerId}`); if (raw) setPhysician(JSON.parse(raw)); } catch { /* none */ }
  }, [partnerId, physician]);
  const branches = useQuery<{ items: PartnerLocation[] }>(
    () => (partnerId ? api.get(`/partners/${partnerId}/locations`) : Promise.resolve({ items: [] })), [partnerId]);
  const venues = useQuery<{ items: CampaignVenue[] }>(() => api.get(`/campaigns/${campaign.id}/venues`), [campaign.id]);

  const perDay = partner?.dailyCapacity ?? 0;
  const days = Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86_400_000) + 1);
  const open = (campaign.progress.openOrderCount ?? 0);

  async function go() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<NonNullable<typeof result>>(`/campaigns/${campaign.id}/arrange`, {
        partnerId, physicianId: physician!.id, from, to, startTime, endTime,
        slotMinutes: Number(slotMinutes), skipSundays,
        ...(locationId ? { partnerLocationId: locationId } : {}),
        ...(venueId ? { campaignVenueId: venueId } : {}),
      }, idem.key());
      idem.rotate();
      setResult(r);
      try { localStorage.setItem(`dru.hr.physicianFor.${partnerId}`, JSON.stringify(physician)); } catch { /* fine */ }
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  if (result) {
    return (
      <Dialog title="Cohort arranged" onClose={onDone} wide>
        <p className="mt-2 text-sm text-neutral-800">
          <strong>{result.booked}</strong> of {result.considered} booked at {result.partner.name}
          {result.routed ? `, ${result.routed} routed on the way` : ''}.{' '}
          <InfoTip label="About check-in passes">
            Every booking issued its own check-in pass. The passes are not shown here — the desk checks a
            campaign cohort in by employee code.
          </InfoTip>
        </p>
        {result.skipped.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-overdue-600">{result.skipped.length} could not be booked:</p>
            <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto">
              {result.skipped.map((s) => (
                <li key={s.employeeCode} className="text-xs text-neutral-700"><span className="font-mono">{s.employeeCode}</span> — {s.reason}</li>
              ))}
            </ul>
            <p className="mt-1 text-xs text-neutral-500">Widen the window, choose another hospital for them, or arrange them one by one from the orders table.</p>
          </div>
        )}
        <div className="mt-5 flex justify-end"><Button onClick={onDone}>Done</Button></div>
      </Dialog>
    );
  }

  return (
    <Dialog title="Route and book the cohort" onClose={onClose} wide>
      <p className="mt-1 text-xs text-neutral-500">
        Every order still waiting on HR — {open} open — is routed to this hospital and booked into
        the next day with room, against its real daily capacity. Employees already booked are left alone.
      </p>
      <div className="mt-3 space-y-3">
        <HospitalPicker value={partner}
          onChange={(p) => { setPartner(p); setPhysician(null); setLocationId(''); }} />
        <EntityCombobox<PhysicianRow> label="Certifying physician" required value={physician} onChange={setPhysician}
          fetchOptions={searchPhysicians} getKey={(p) => p.id}
          placeholder="Search the certifying panel…" searchPlaceholder="Name or registration number"
          emptyMessage="No AFIH-certified physician matches that."
          renderOption={(p) => <><span className="block font-medium text-neutral-900">{p.fullName}</span><span className="block font-mono text-caption font-normal text-neutral-500">{p.registrationNo}</span></>}
          renderValue={(p) => `${p.fullName} — ${p.registrationNo}`} />
        {otherBranches(branches.data?.items).length > 0 && (
          <SearchSelect label="Branch" value={locationId} onChange={setLocationId}
            placeholder="Registered site" searchPlaceholder="Search branches by name or city…"
            options={[
              { value: '', label: 'Registered site' },
              ...otherBranches(branches.data?.items).map((b) => ({ value: b.id, label: b.name, hint: b.city ?? undefined })),
            ]} />
        )}
        {(venues.data?.items.length ?? 0) > 0 && (
          <SearchSelect label="Camp venue" value={venueId} onChange={setVenueId} info="Booked as camp visits at this venue; the hospital above runs it."
            placeholder="Not at a camp — at the hospital" searchPlaceholder="Search venues by name or city…"
            options={[
              { value: '', label: 'Not at a camp — at the hospital' },
              ...venues.data!.items.map((v) => ({ value: v.id, label: v.name, hint: v.city ?? undefined })),
            ]} />
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label="From" type="date" value={from} onChange={setFrom} />
          <TextField label="To" type="date" value={to} onChange={setTo} />
          <TextField label="Day starts" type="time" value={startTime} onChange={setStartTime} />
          <TextField label="Day ends" type="time" value={endTime} onChange={setEndTime} />
          <SelectField label="Slot length" value={slotMinutes} onChange={setSlotMinutes}>
            {['15', '20', '30', '45', '60'].map((m) => <option key={m} value={m}>{m} minutes</option>)}
          </SelectField>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-neutral-800">
            <input type="checkbox" checked={skipSundays} onChange={(e) => setSkipSundays(e.target.checked)} /> Skip Sundays
          </label>
        </div>
        {partner && (
          <p className="text-xs text-neutral-600">
            {partner.name} takes {perDay} a day; {days} day{days === 1 ? '' : 's'} in this window is room for about{' '}
            <strong>{perDay * (skipSundays ? Math.round(days * 6 / 7) : days)}</strong> — before whatever is already booked there.
          </p>
        )}
      </div>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={go} loading={busy} disabled={!partner || !physician || to < from}>
          {busy ? 'Booking the cohort…' : `Route and book ${open}`}
        </Button>
      </div>
    </Dialog>
  );
}

/** The slots booked under this campaign, soonest first. */
function CampaignAppointments({ rows }: {
  rows: { data: CursorPage<AppointmentRow> | null; loading: boolean; error: string | null; reload: () => void };
}) {
  const nav = useNavigate();
  return (
    <section className="space-y-2">
      <h2 className="text-caption font-semibold uppercase tracking-wider text-neutral-700">
        Appointments{rows.data ? ` (${rows.data.items.length})` : ''}
      </h2>
      <DataTable<AppointmentRow>
        rows={rows.data?.items ?? []} loading={rows.loading} error={rows.error} onRetry={rows.reload} rowKey={(a) => a.id}
        onRowClick={(a) => nav(`/hr/appointments/${a.id}`)}
        empty={{ title: 'Nothing booked yet', hint: 'Slots appear here as employees in the cohort are booked.' }}
        columns={[
          { key: 'who', header: 'Employee', alwaysVisible: true, cell: (a) => (
            <div>
              <Link to={`/hr/appointments/${a.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-neutral-900 hover:text-primary-700">
                {a.order?.employee?.fullName ?? '—'}
              </Link>
              {a.order?.employee?.employeeCode && <div className="font-mono text-caption text-neutral-500">{a.order.employee.employeeCode}</div>}
            </div>
          ) },
          { key: 'when', header: 'When', cell: (a) => <span className="text-small tabular-nums">{formatDateTime(a.scheduledFor)}</span> },
          { key: 'venue', header: 'Hospital', cell: (a) => <span className="text-small text-neutral-600">{a.partner?.name ?? '—'}</span> },
          { key: 'status', header: 'Status', cell: (a) => <StatusBadge tone={a.status === 'COMPLETED' || a.status === 'CHECKED_IN' ? 'covered' : a.status === 'NO_SHOW' || a.status === 'CANCELLED' ? 'overdue' : 'pending'}>{APPOINTMENT_STATUS_LABEL[a.status] ?? a.status}</StatusBadge> },
        ]}
      />
    </section>
  );
}

export function Campaigns() {
  return <RequirePermission need="campaign.read" title="Campaigns" what="Campaigns"><CampaignsPage /></RequirePermission>;
}

export function CampaignDetailPage() {
  return <RequirePermission need="campaign.read" title="Campaign" what="Campaigns"><CampaignDetailPagePage /></RequirePermission>;
}
